import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, MapPin, BatteryCharging, Building2 } from 'lucide-react';
import { trackEvent, EVENTS } from '@/lib/analytics';

const CASE_STUDIES = [
  {
    id: 'tfl-betul',
    title: 'Technocraft Fashions, Betul',
    subtitle: 'A battery instead of a diesel generator',
    location: 'Betul, Madhya Pradesh',
    capacity: '100 kW / 96 kWh',
    type: 'Manufacturing',
    description: 'The plant needed reliable backup for critical operations through grid outages. Rather than adding a diesel generator, it deployed a Voltide BESS — keeping production running and removing diesel cost and upkeep from the plan.',
    image: '/images/betul-hero.jpg',
    imageContain: true,
    results: ['Minimum downtime via automatic islanding', 'Zero diesel generators added', '100% of critical loads covered', 'Live remote monitoring with outage logs'],
  },
  {
    id: 'uru',
    title: 'Uru Brewpark',
    subtitle: "India's First Diesel-Free Large-Format Restaurant",
    location: 'Bangalore, India',
    capacity: '482 kWh',
    type: 'Restaurant',
    description: 'Uru Brewpark became India\'s first large-format restaurant to eliminate diesel generators entirely through a Voltide-structured OPEX model. The restaurant transitioned from 2 large diesel generators to a 482 kWh BESS without upfront capital expenditure.',
    image: '/images/uru-hero.jpg',
    results: ['100% diesel generator elimination', 'Zero emissions backup power', 'No upfront capital expenditure', 'Silent operation in urban setting'],
  },
];

export default function CaseStudiesPage() {
  return (
    <div data-testid="case-studies-page">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="voltide-container">
          <div className="max-w-3xl animate-fade-up">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-blue-50 text-[#0061DD] border-blue-100 mb-6">
              Case Studies
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6">
              Real results from <span className="blue-gradient-text">real deployments</span>
            </h1>
            <p className="text-base lg:text-lg text-[#57534E] leading-relaxed max-w-2xl">
              See how businesses across India are switching from diesel to clean battery energy storage with Voltide.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies List */}
      <section className="pb-20 lg:pb-24" data-testid="case-studies-list">
        <div className="voltide-container">
          {CASE_STUDIES.map((cs) => (
            <Link
              key={cs.id}
              to={`/case-studies/${cs.id}`}
              className="block"
              data-testid={`case-study-link-${cs.id}`}
              onClick={() => trackEvent(EVENTS.VIEW_CASE_STUDY, { study: cs.id })}
            >
              <div className="bento-card group mb-8 last:mb-0">
                <div className="grid lg:grid-cols-2">
                  <div className="aspect-[4/3] lg:aspect-auto overflow-hidden bg-[#F9F9F7]">
                    <img
                      src={cs.image}
                      alt={cs.title}
                      className={`w-full h-full sketch-img group-hover:scale-105 transition-transform duration-500 ${cs.imageContain ? 'object-contain p-4' : 'object-cover'}`}
                    />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge className="rounded-full bg-blue-50 text-[#0061DD] border-blue-100">{cs.type}</Badge>
                      <Badge variant="secondary" className="rounded-full bg-stone-100 text-[#57534E]">
                        <MapPin className="w-3 h-3 mr-1" /> {cs.location}
                      </Badge>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2">{cs.title}</h2>
                    <p className="text-lg text-[#0061DD] font-medium mb-4">{cs.subtitle}</p>
                    <p className="text-[#57534E] leading-relaxed mb-6">{cs.description}</p>

                    <div className="flex flex-wrap gap-6 mb-6">
                      <div className="flex items-center gap-2">
                        <BatteryCharging className="w-5 h-5 text-[#0061DD]" />
                        <span className="mono text-sm font-semibold text-[#1A1A1A]">{cs.capacity}</span>
                      </div>
                    </div>

                    <ul className="space-y-2 mb-6">
                      {cs.results.map(r => (
                        <li key={r} className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {r}
                        </li>
                      ))}
                    </ul>

                    <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white gap-2 w-fit group-hover:gap-3 transition-all" data-testid={`case-study-read-${cs.id}`}>
                      Read Full Case Study <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-[#0061DD]">
        <div className="voltide-container text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Want similar results for your facility?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto">
            Let our team design a custom BESS solution for you.
          </p>
          <Link to="/contact">
            <Button className="rounded-full bg-white text-[#0061DD] hover:bg-blue-50 px-8 py-6 text-lg font-semibold gap-2" data-testid="case-studies-cta-btn">
              Get a Quote <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
